import React, { useEffect, useRef, useState } from 'react';
import {
  BackHandler,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import PageHeader from '../components/account/PageHeader';
import BrandMark from '../components/payment/BrandMark';
import MethodRow from '../components/payment/MethodRow';
import PaymentResult from '../components/payment/PaymentResult';
/*
 * The stand-in gateway is no longer mounted anywhere.
 *
 * `components/payment/RazorpayCheckout` drew a convincing gateway page and
 * let a 2.4 second timer decide the outcome. That was honest while no keys
 * existed and became dangerous the moment they did - a customer told
 * "Payment Successful" with nothing charged, on a booking that then expired.
 *
 * The real sheet is native and draws itself over the app; there is no
 * element to render. See `services/razorpay`. The file is left on disk as
 * the reference for what this flow used to be.
 */
import Button from '../components/ui/Button';
import Icon from '../components/ui/Icon';
import Text from '../components/ui/Text';
import { Booking } from '../data/bookings';
import {
  countdown,
  DEFAULT_METHOD,
  defaultCard,
  GATEWAY,
  PAYMENT_WINDOW_SECONDS,
  PaymentMethodKey,
  PaymentOrder,
  paymentMethods,
  refundIdFor,
  rupees,
  savedCards,
  SavedCard,
  SECURE_NOTE,
  stamp,
  upiApps,
  WINDOW_WARNING_SECONDS,
} from '../data/payment';
import { onlinePaymentsEnabled } from '../services/config';
import { payForCheckout, type PayOutcome } from '../services/payFlow';
import {
  colors,
  hairline,
  radii,
  screenPadding,
  shadows,
  spacing,
  useResponsive,
} from '../theme';

/**
 * Paying for a booking, from the summary to the receipt.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS SCREEN IS
 * ---------------------------------------------------------------------------
 * One screen holding nine states, because there is no navigator and every one
 * of them is reached from the state before it. `stage` is the whole routing
 * story, the same way `open` is in `ChatScreen`.
 *
 *   summary      what you are about to book               (arrives from cart)
 *   placing      the booking is being created
 *   method       what you want to pay with
 *   saved-cards  which stored card, a detour from method
 *   verifying    the gateway said yes; we have not agreed yet
 *   success      booked
 *   failed       the gateway said no, with its reason
 *   expired      the window ran out with nothing paid
 *   refunded     paid, but after the window had already closed
 *
 * The gateway's own page is a modal over whichever stage raised it, not a
 * stage - see `RazorpayCheckout`, which is a stand-in for the SDK.
 *
 * ---------------------------------------------------------------------------
 * TODAY THE MIDDLE OF THAT LIST IS SKIPPED
 * ---------------------------------------------------------------------------
 * `onlinePaymentsEnabled()` is the server's answer, fetched at launch - see
 * `services/config`. While it is false no gateway is wired up, so a booking
 * can only be `cash`, settled with the professional afterwards. The summary
 * therefore goes straight to `placing`, and `method` through `refunded` are
 * unreachable. They are left standing rather than deleted: nothing about them
 * is wrong, they are simply waiting on credentials.
 *
 * Everything the customer reads while the flag is false says the money is owed
 * on completion, and nothing claims a payment was taken.
 *
 * ---------------------------------------------------------------------------
 * THE TWO RULES THAT SHAPE IT
 * ---------------------------------------------------------------------------
 * 1. A GATEWAY SAYING YES IS NOT A PAYMENT. The callback comes from the
 *    customer's own device, so it is a claim, not proof. `verifying` stands
 *    between the claim and the booking, and the customer is shown that it is
 *    happening rather than being spun at - the wait IS the safeguard.
 *
 * 2. THE SLOT IS HELD, NOT RESERVED FOREVER. An abandoned checkout otherwise
 *    blocks a professional's morning, so the booking lapses after
 *    `PAYMENT_WINDOW_SECONDS`. The countdown is on screen from the first page,
 *    because a deadline you are only told about once it has passed is a trap.
 *
 * Those two produce the awkward case the flow has to handle honestly: money
 * arriving after the window shut. It does not silently become a booking and it
 * does not silently vanish - it becomes `refunded`.
 *
 * ---------------------------------------------------------------------------
 * WHAT IS NOT REAL
 * ---------------------------------------------------------------------------
 * No gateway, no server, no verification. `verifying` is a timer, and no
 * refund is initiated by anything. A successful payment also does NOT append a
 * booking to `src/data/bookings.ts` - that list is module-level seed data - so
 * "View Booking Details" lands on the bookings tab showing the seeded ones.
 * Lifting that array into state is the next piece of work this flow needs.
 */

type Stage =
  | 'summary'
  | 'method'
  | 'saved-cards'
  | 'placing'
  | 'plan-created'
  /**
   * The booking exists and is being paid for.
   *
   * New, and the shape of the whole online path changed to make room for
   * it: the booking is now placed BEFORE any money is asked for, because
   * the amount is read off the booking server-side and there is nothing to
   * open a gateway order against until it exists.
   */
  | 'paying'
  /**
   * Backed out of the gateway sheet. Distinct from `failed`: nothing went
   * wrong and nothing was charged, so "try again" is the wrong sentence.
   */
  | 'payment-cancelled'
  | 'success'
  | 'failed'
  | 'booking-failed'
  | 'expired'
  | 'refunded';

/** Stages after which the window no longer matters. */
const SETTLED: Stage[] = [
  'placing',
  'plan-created',
  'paying',
  'payment-cancelled',
  'success',
  'booking-failed',
  'expired',
  'refunded',
];

/** What placing the basket produced: its bookings, and the checkout that pays for them. */
export type PlacedOrder = {
  bookings: Booking[];
  /** Paid through as one - see `payForCheckout`. */
  checkoutId: string;
};

type Props = {
  order: PaymentOrder;
  /**
   * Already paid, from the cart: open on the receipt.
   *
   * Online, "Proceed to Pay" opens Razorpay over the cart itself - see
   * `HomeScreen`'s `payFromCart` - and the server books the basket only once
   * it has confirmed the payment. This screen is shown after that, with the
   * bookings the payment made. Without it the screen opens on its summary,
   * which is how cash and recurring orders are confirmed.
   */
  paid?: { bookings: Booking[]; at: Date };
  /** Back out to the cart, with nothing paid. Only from the summary. */
  onBack: () => void;
  /**
   * Paid and verified, with where they chose to go next.
   *
   * Hands over the booking(s) the order became rather than a bare signal: the
   * caller has to both record it and open it, and building it twice is how the
   * list and the page it opens end up disagreeing. An array because the
   * platform books one row PER SERVICE - a basket of three is three bookings.
   *
   * `land` rather than a second callback, because the two differ only in where
   * they end up - and the part that must NOT differ is that both record the
   * booking and empty the basket. A "Go to Home" that skipped either would
   * leave a paid-for basket sitting there ready to be paid for again.
   */
  onPaid: (bookings: Booking[], land: 'booking' | 'home') => void;
  /**
   * Turns a paid order into real bookings - `POST /bookings`.
   *
   * Called once verification finishes, not before: a booking must not exist
   * against a payment that has not settled. Left to throw on failure -
   * `stage` becomes `'booking-failed'` and the money is already taken, which
   * is why that stage reads differently from a plain payment failure.
   */
  onCreateBooking: (order: PaymentOrder) => Promise<PlacedOrder>;
  /**
   * The bookings a paid basket became, by id.
   *
   * Online, placing the basket books NOTHING - the server books it once the
   * payment is captured - so the receipt's bookings are fetched after, not
   * returned by `onCreateBooking`.
   */
  onLoadBookings?: (ids: string[]) => Promise<Booking[]>;
  /**
   * Sets up the repeating arrangement - `POST /bookings/recurring-plans`.
   *
   * Separate from `onCreateBooking` because it does not produce one: the
   * server creates each visit when it falls due, so there is no booking to
   * hand back and the screen lands somewhere else because of it.
   */
  onCreatePlan: (order: PaymentOrder) => Promise<void>;
  /** Give up, from the end states where nothing was booked. */
  onHome: () => void;
  /** "Need Help?" - support lives in Account. */
  onHelp?: () => void;
};

function PaymentScreen({
  order,
  paid,
  onBack,
  onPaid,
  onCreateBooking,
  onLoadBookings,
  onCreatePlan,
  onHome,
  onHelp,
}: Props) {
  const insets = useSafeAreaInsets();
  /*
   * Checkout is a form and a list of amounts - the two things that read worst
   * across a wide screen, where a label and its figure end up a hand's width
   * apart. `resp.page` holds the content to 560pt beyond a phone AND carries
   * the page gutter, which is why the scrollers use it rather than
   * `resp.column`: the rows here have no padding of their own, so `column`
   * alone left every line flush against the screen edge.
   */
  const resp = useResponsive();

  const [stage, setStage] = useState<Stage>(paid ? 'success' : 'summary');
  const [method, setMethod] = useState<PaymentMethodKey>(DEFAULT_METHOD);
  /* Annotated on the callback rather than only on the hook: with a lazy
     initializer TypeScript matches the `SetStateAction` overload first and
     tries to read `() => SavedCard | null` as `() => SavedCard`, which the
     `null` return does not satisfy. Saying what the initializer produces
     resolves it. `null` is reachable - there may be no saved cards at all. */
  const [card, setCard] = useState<SavedCard | null>(
    (): SavedCard | null => defaultCard(),
  );


  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);

  /*
   * `instrument` and `failure` lived here.
   *
   * Both were written by the stand-in gateway and by nothing else. The real
   * sheet does not tell the app which card or UPI handle was used - that is
   * the gateway’s record, and Razorpay’s dashboard is where support reads
   * it - so the receipt no longer claims to know. `payOutcome` carries the
   * failure reason the server or the SDK actually gave.
   */
  const [settledAt, setSettledAt] = useState<Date | null>(paid?.at ?? null);


  /** What `createBooking` returned - the receipt reads from this, not a guess. */
  const [madeBookings, setMadeBookings] = useState<Booking[]>(
    paid?.bookings ?? [],
  );
  /** Why booking creation failed, for the `booking-failed` stage. */
  const [bookingError, setBookingError] = useState<string | null>(null);
  /** How the payment run ended. Drives the `failed` copy. */
  const [payOutcome, setPayOutcome] = useState<PayOutcome | null>(null);
  /** The checkout the bookings were placed under - the one thing paid for. */
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  /** Guards the payment run against an effect re-firing mid-flight. */
  const payRequested = useRef(false);
  /**
   * Guards against firing `onCreateBooking` twice for one `verifying` visit.
   *
   * The effect below re-runs whenever its dependencies change identity, and a
   * booking call is not idempotent from here - retrying it is `stage` moving
   * back to `verifying` on purpose (see `booking-failed`), not the effect
   * firing again on its own.
   */
  const bookingRequested = useRef(false);
  /*
   * False once the screen is gone - the only thing allowed to drop a
   * request's result.
   *
   * The effects below used to cancel their own request whenever their
   * dependencies changed identity. `onCreateBooking` is rebuilt on every
   * render of `HomeScreen`, and placing an order re-renders it, so the request
   * cancelled itself mid-flight: its answer landed on a cancelled closure, and
   * the screen sat on "Confirming your booking" for ever - the stuck payment.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /*
   * The deadline, fixed at mount.
   *
   * An absolute instant rather than a counter that ticks down. A counter loses
   * time whenever the interval is throttled - backgrounding the app for five
   * minutes would leave the window five minutes longer than it should be, and
   * the one promise this screen makes is that the slot is only held so long.
   */
  const deadline = useRef(Date.now() + PAYMENT_WINDOW_SECONDS * 1000);
  const [left, setLeft] = useState(PAYMENT_WINDOW_SECONDS);

  const settled = SETTLED.includes(stage);

  /*
   * Back, refused outright while a booking is being made.
   *
   * ---------------------------------------------------------------------------
   * THE COMMENT IN `HomeScreen` USED TO SAY THIS WAS ALREADY HANDLED
   * ---------------------------------------------------------------------------
   * It was not, twice over. This screen registered no back handler at all, and
   * `placing` and `verifying` are plain screens rather than `Modal`s, so
   * neither of the two things that comment relied on existed. A back press from
   * either fell straight through to `HomeScreen`'s `unwind`, which drops
   * `payOrder` and lands on the cart.
   *
   * On the summary that is the right answer and it stays. On these two it is
   * not: `onCreateBooking` is already in flight, it is not idempotent, and the
   * request does not stop because the screen it was fired from went away. The
   * booking is created server-side, the customer is looking at their basket,
   * and the confirmation they should have seen is never drawn - so they either
   * pay for the same thing twice or ring support about a booking they cannot
   * see.
   *
   * `true` unconditionally, then, and no way out. There is no half-measure
   * available: a "leave anyway" would be an offer to abandon a booking that is
   * already being made. The screen leaves on its own the moment the request
   * settles, into `success`, `plan-created` or `booking-failed`, and every one
   * of those has its own exit.
   */
  /*
   * `paying` joins these two. The booking is already made and the gateway
   * sheet may be open over the app - leaving would abandon a customer
   * mid-payment on a booking that already exists.
   */
  const holding = stage === 'placing' || stage === 'paying';

  useEffect(() => {
    if (!holding) {
      return;
    }
    const sub = BackHandler.addEventListener('hardwareBackPress', () => true);
    return () => sub.remove();
  }, [holding]);

  /*
   * The window exists to stop an abandoned CHECKOUT from holding a
   * professional's morning. With nothing to pay there is no checkout to
   * abandon - the booking is placed on the next tap - so the clock does not
   * run and a booking cannot expire out from under the customer.
   */
  const expired = onlinePaymentsEnabled() && left <= 0;

  useEffect(() => {
    if (settled || !onlinePaymentsEnabled()) {
      return;
    }
    const tick = () => {
      setLeft(Math.max(0, Math.round((deadline.current - Date.now()) / 1000)));
    };
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [settled]);

  /*
   * Out of time, with nothing paid.
   *
   * Deliberately allowed to fire while the gateway modal is up: money may
   * still be in flight, and pretending otherwise is what produces a payment
   * with no booking behind it. The settle handler below reads `expired` and
   * routes to `refunded` rather than to `success`.
   */
  useEffect(() => {
    // `failed` is not in `SETTLED` on purpose: a failure is still retryable,
    // so the window keeps running underneath it and takes the slot away here
    // like it would from any other unpaid stage.
    if (expired && !settled) {
      setStage('expired');
      setSettledAt(new Date());
    }
  }, [expired, settled]);

  /*
   * `onSettled` and `openGateway` lived here.
   *
   * Both existed only to drive the stand-in gateway: one opened it, the
   * other read the outcome its timer produced. The real flow is a run of
   * three server calls per booking and lives in `services/payFlow`, driven
   * by the `paying` stage rather than by a callback from a modal.
   */


  const paidOn = settledAt ? stamp(settledAt) : '';

  /**
   * Whether the money may have moved despite the failure.
   *
   * Set by `payFlow` for a network drop or a verify call that never landed -
   * the cases where Razorpay’s webhook can still complete the booking after
   * this screen has given up. A decline is not uncertain, and must not read
   * as though it might be.
   */
  const uncertainPayment =
    payOutcome?.status === 'failed' && payOutcome.uncertain;

  /* The same instant, under the word that is true when nothing was charged.
     `settledAt` is stamped by the gateway on the paid path and by Confirm on
     the cash one, so both receipts date themselves from the same field. */
  const bookedOn = paidOn;

  /**
   * The verification steps, one at a time - then the booking, then the
   * receipt.
   *
   * Booking happens here, not in the `success` branch below: `success` is a
   * render, and a render is not the place to fire a network request. This
   * runs once, exactly when the last step ticks over. Placed after `paidWith`
   * is declared, since it reads that value - an effect earlier in the
   * component referencing a `const` declared later is the exact temporal-
   * dead-zone crash this app hit once already.
   */
  useEffect(() => {
    if (stage !== 'placing') {
      bookingRequested.current = false;
      return;
    }

    if (bookingRequested.current) {
      return;
    }
    bookingRequested.current = true;

    /*
     * Recurring creates a PLAN, not a booking.
     *
     * The server generates and prices each visit when it comes due, so there
     * is nothing yet for the bookings list to show - which is why this lands
     * on its own result rather than the booking one. It previously invented a
     * booking client-side and called it confirmed; that put a job on screen
     * that no professional was ever going to be sent to.
     */
    if (order.mode === 'recurring') {
      onCreatePlan(order)
        .then(() => {
          if (!alive.current) return;
          setStage('plan-created');
        })
        .catch(thrown => {
          if (!alive.current) return;
          setBookingError(
            thrown instanceof Error
              ? thrown.message
              : 'Could not set up your recurring plan.',
          );
          setStage('booking-failed');
        });
      return;
    }

    onCreateBooking(order)
      .then(({ bookings: made, checkoutId: placed }) => {
        if (!alive.current) return;
        setMadeBookings(made);
        setCheckoutId(placed);

        /*
         * Online bookings are created UNPAID and sit in `awaiting_payment`
         * until money lands - they do not reach dispatch on their own. So
         * this is the middle of the flow, not the end of it.
         *
         * The screen used to run a gateway first and book afterwards, which
         * could never work against a real Razorpay: the order is minted per
         * booking, server-side, from the booking’s own amount, and there is
         * no id to mint it against before the booking exists.
         */
        /*
         * Online, nothing is booked yet: the checkout waits for its money and
         * the server books it once Razorpay captures the payment. So an
         * online placement with no bookings is the normal case, and goes
         * straight to paying. One that came back WITH bookings cost nothing
         * and is already confirmed.
         */
        if (made.length === 0) {
          setPayOutcome(null);
          setStage('paying');
          return;
        }

        setStage('success');
      })
      .catch(thrown => {
        if (!alive.current) return;
        setBookingError(
          thrown instanceof Error
            ? thrown.message
            : 'Could not confirm your booking.',
        );
        setStage('booking-failed');
      });
  }, [stage, order, onCreateBooking, onCreatePlan]);

  /**
   * The payment run: gateway order, Razorpay’s sheet, then verification -
   * ONCE for the whole checkout, however many bookings it produced.
   *
   * All three steps live in `services/payFlow`, including the part that
   * matters most and is easiest to leave out: after any failure other than
   * the customer backing out, it asks the server whether the booking was
   * paid anyway. Razorpay’s webhook reaches the server whether or not this
   * app is still running, so a dropped connection in the last half second
   * is a paid booking, not a failed one.
   *
   * Re-entering this stage is how Retry works. It is safe: the server hands
   * back the order still open for the same bookings, and refuses one that is
   * already paid.
   */
  useEffect(() => {
    if (stage !== 'paying') {
      payRequested.current = false;
      return;
    }
    if (payRequested.current || !checkoutId) return;
    payRequested.current = true;

    // Counted from the basket: its bookings do not exist until it is paid.
    payForCheckout(checkoutId, order.items.length)
      .then(outcome => {
        if (!alive.current) return;
        setPayOutcome(outcome);
        setSettledAt(new Date());

        if (outcome.status === 'paid') {
          const ids = outcome.bookingIds ?? [];
          if (ids.length === 0 || !onLoadBookings) {
            setStage('success');
            return;
          }
          /* Shown paid either way: the money has landed, and a booking that
             could not be fetched this second is on the list at next refresh. */
          onLoadBookings(ids)
            .then(made => {
              if (alive.current) setMadeBookings(made);
            })
            .catch(() => {})
            .finally(() => {
              if (alive.current) setStage('success');
            });
          return;
        }
        setStage(outcome.status === 'cancelled' ? 'payment-cancelled' : 'failed');
      })
      .catch(thrown => {
        /*
         * `payForCheckout` returns its failures rather than throwing, so
         * anything landing here is a defect in this app, not a payment
         * problem. Still routed somewhere with an exit - a customer holding
         * a created booking must never be left on a dead screen.
         */
        if (!alive.current) return;
        setPayOutcome({
          status: 'failed',
          paidCount: 0,
          total: order.items.length,
          message:
            thrown instanceof Error
              ? thrown.message
              : 'Something went wrong while taking the payment.',
          uncertain: true,
        });
        setSettledAt(new Date());
        setStage('failed');
      });
  }, [stage, order.items.length, checkoutId, onLoadBookings]);

  // ------------------------------------------------------------ end states

  if (stage === 'placing') {
    return (
      <PaymentResult
        tone="neutral"
        icon="clock"
        title="Confirming your booking"
        blurb={
          onlinePaymentsEnabled()
            ? 'Holding your slot.\nRazorpay opens in a moment.'
            : 'Holding your slot and finding a professional.\nThis takes a moment.'
        }
        note={
          onlinePaymentsEnabled()
            ? 'You pay once, for your whole order.'
            : 'Nothing is charged now - you pay once the work is done.'
        }
      />
    );
  }

  if (stage === 'plan-created') {
    return (
      <PaymentResult
        tone="success"
        icon="checkCircle"
        title="Recurring plan set"
        blurb={`Your ${order.title} will repeat as scheduled. Each visit is booked and priced when it comes due.`}
        rows={[
          { label: 'Repeats', value: order.cadence ?? '-' },
          { label: 'First visit', value: order.when },
          { label: 'Amount per visit', value: rupees(order.total) },
          { label: 'Pay', value: 'On completion of each visit' },
        ]}
        note="Each visit appears in your bookings shortly before it is due. You can pause or cancel the plan any time."
        actions={
          <>
            {/* No booking to open - the first visit does not exist yet, and
                offering "View Booking Details" would land on nothing. */}
            <Button label="Go to Home" onPress={() => onPaid([], 'home')} />
            {onHelp ? (
              <Button label="Need Help?" variant="ghost" onPress={onHelp} />
            ) : null}
          </>
        }
      />
    );
  }

  if (stage === 'paying') {
    const many = order.items.length > 1;

    return (
      <PaymentResult
        tone="neutral"
        icon="shieldCheck"
        title="Complete your payment"
        blurb={
          many
            ? `One payment for all ${order.items.length} services in your order. Razorpay is opening.`
            : 'Razorpay is opening. Your booking is confirmed as soon as you pay.'
        }
        rows={[
          { label: 'Order ID', value: order.reference },
          { label: 'Amount', value: rupees(order.total) },
        ]}
        note="We never mark a payment successful until our server has confirmed it with Razorpay."
      />
    );
  }

  /*
   * Backed out of the sheet. The booking EXISTS and is unpaid - which is the
   * whole reason this is not the `failed` screen: nothing went wrong, and
   * the one thing the customer must not be left thinking is that the booking
   * went away with the sheet.
   */
  if (stage === 'payment-cancelled') {

    return (
      <PaymentResult
        tone="neutral"
        icon="clock"
        title="Payment not completed"
        blurb="Nothing has been booked and nothing was charged. Pay to confirm your booking."
        rows={[
          { label: 'Order ID', value: order.reference },
          { label: 'Amount', value: rupees(order.total) },
        ]}
        actions={
          <>
            <Button
              label="Pay now"
              onPress={() => {
                setPayOutcome(null);
                setStage('paying');
              }}
            />
            {/* Nothing was booked, so there is no booking to open - back to the
                basket, which is still exactly as they left it. */}
            <Button label="Back to Cart" variant="outline" onPress={onBack} />
            {onHelp ? (
              <Button label="Need Help?" variant="ghost" onPress={onHelp} />
            ) : null}
          </>
        }
      />
    );
  }

  /*
   * The verifying stage was here, and the paying stage above replaced it.
   *
   * It walked three captioned steps on a timer while nothing was being
   * confirmed with anybody. Real verification is one server call inside the
   * payment run, and it has either landed or it has not - a progress list over
   * it would be the same theatre with a real request behind it.
   */

  if (stage === 'success') {
    /* Paid from the cart is paid online, whatever the flag says now. */
    const paidOnline = paid !== undefined || onlinePaymentsEnabled();
    return (
      <PaymentResult
        tone="success"
        icon="checkCircle"
        title={paidOnline ? 'Payment Successful!' : 'Booking Confirmed!'}
        blurb={
          paidOnline
            ? 'Your booking is confirmed.'
            : 'Your booking is confirmed. Pay once the work is done.'
        }
        /* Amount "due", not "paid", and no instrument - nothing was charged,
           and a receipt shaped like a paid one is the whole thing this flow
           was getting wrong. */
        rows={
          paidOnline
            ? [
                { label: 'Paid Amount', value: rupees(order.total) },
                /* The booking number, not the client-side `order.orderId`:
                   that string is minted in the app and means nothing to
                   support or to Razorpay. How it was paid is not shown at
                   all - the gateway holds that, and guessing it on a receipt
                   is worse than leaving it off. */
                {
                  label: madeBookings.length > 1 ? 'Bookings' : 'Booking ID',
                  /* The platform's own numbers once the payment has made
                     them - `order.reference` is minted in the app and means
                     nothing to support. */
                  value:
                    madeBookings.length === 0
                      ? order.reference
                      : madeBookings.length === 1
                      ? madeBookings[0].reference
                      : `${madeBookings[0].reference} +${madeBookings.length - 1} more`,
                },
                { label: 'Paid On', value: paidOn },
              ]
            : [
                { label: 'Amount Due', value: rupees(order.total) },
                { label: 'Pay', value: 'On completion' },
                { label: 'Booking ID', value: order.reference },
                { label: 'Booked On', value: bookedOn },
              ]
        }
        actions={
          <>
            <Button
              label="View Booking Details"
              onPress={() => onPaid(madeBookings, 'booking')}
            />
            {/* Not `onHome`: that one is for the ends where nothing was
                booked. This is still a completed payment. */}
            <Button
              label="Go to Home"
              variant="ghost"
              onPress={() => onPaid(madeBookings, 'home')}
            />
          </>
        }
      />
    );
  }

  if (stage === 'booking-failed') {
    return (
      <PaymentResult
        tone="danger"
        icon="alert"
        title="Booking not confirmed"
        /*
         * Nothing has been charged, in EITHER mode.
         *
         * This screen used to say "Payment received, booking not confirmed"
         * for online, which was true of the old order of operations - pay
         * first, book after. It is now exactly backwards: the booking is
         * placed before a gateway is opened, so a failure here is a failure
         * to create it and no money has been asked for yet. Telling somebody
         * we hold a payment we never took is the worst sentence on this
         * screen.
         */
        blurb="We could not confirm your booking. Nothing has been charged - retry, or reach support and quote the booking ID."
        rows={[
          { label: 'Amount', value: rupees(order.total) },
          { label: 'Booking ID', value: order.reference },
        ]}
        notice={
          bookingError
            ? { title: 'Booking error', detail: bookingError }
            : undefined
        }
        actions={
          <>
            <Button
              label="Retry"
              onPress={() => {
                setBookingError(null);
                /* One stage books, in both modes. `placeCheckoutOrder` is
                   sent with the order’s own idempotency key, so a retry that
                   is really a repeat returns the order already placed rather
                   than making a second one. */
                setStage('placing');
              }}
            />
            {/* The summary is skipped online, so this is the way back to the
                cart - nothing was booked and nothing was charged. */}
            <Button label="Back to Cart" variant="outline" onPress={onBack} />
            {onHelp ? (
              <Button label="Contact Support" variant="ghost" onPress={onHelp} />
            ) : null}
          </>
        }
      />
    );
  }

  if (stage === 'failed') {
    return (
      <PaymentResult
        tone="danger"
        icon="xCircle"
        title={uncertainPayment ? 'We could not confirm your payment' : 'Payment Failed'}
        /*
         * Two different failures, and conflating them is expensive in both
         * directions.
         *
         * A DECLINE is settled: the bank said no, nothing was charged, try
         * again. An UNCERTAIN failure is a payment the gateway may well have
         * taken while our connection dropped - Razorpay’s webhook completes
         * such a booking on its own, minutes later. Telling that customer
         * "payment failed" is what makes them pay twice.
         */
        blurb={
          uncertainPayment
            ? 'Your payment may still have gone through. Check your bookings in a minute before paying again - if it was taken, the booking confirms itself.'
            : "We couldn't process your payment. Nothing has been charged."
        }
        rows={[
          { label: 'Order ID', value: order.reference },
          { label: 'Amount', value: rupees(order.total) },
          { label: 'Attempted On', value: paidOn },
        ]}
        /*
         * No reason shown, deliberately.
         *
         * This carried `payOutcome.message`, which is whatever string the SDK
         * or the API threw - a gateway error code, a stack-shaped sentence, an
         * HTTP message. None of it is written for a customer, and a code where
         * an explanation should be reads as a broken app rather than a refused
         * card.
         *
         * The blurb above already says the only two things that are both true
         * and actionable: whether money moved, and what to do next. The real
         * reason is in Razorpay’s dashboard, where support can read it.
         */
        note="Nothing is booked until your payment succeeds."
        actions={
          <>
            {/* Straight back into the payment run - one sheet, for the whole
                order. An order already paid is refused, not charged again. */}
            <Button
              label="Try Again"
              onPress={() => {
                setPayOutcome(null);
                setStage('paying');
              }}
            />
            {/* Nothing was booked - back to the basket, not to a booking. */}
            <Button label="Back to Cart" variant="outline" onPress={onBack} />
            {onHelp ? (
              <Button label="Need Help?" variant="ghost" onPress={onHelp} />
            ) : null}
          </>
        }
      >
        {/* The gateway can be reopened from here, so it has to be mounted
            here too - not only under the method list. */}
        {gateway()}
      </PaymentResult>
    );
  }

  if (stage === 'expired') {
    return (
      <PaymentResult
        tone="danger"
        icon="stopwatch"
        title="Payment time ran out"
        blurb="Nothing was booked and nothing was charged."
        rows={[
          { label: 'Order ID', value: order.reference },
          { label: 'Expired On', value: paidOn },
        ]}
        note="If you did pay just now, your booking is confirmed automatically - check your bookings."
        actions={
          <>
            <Button label="Back to Cart" onPress={onBack} />
            <Button label="Go to Home" variant="ghost" onPress={onHome} />
          </>
        }
      >
        {gateway()}
      </PaymentResult>
    );
  }

  if (stage === 'refunded') {
    return (
      <PaymentResult
        tone="success"
        icon="refresh"
        title="Payment Received"
        blurb="Your payment was received after the booking expired."
        rows={[
          { label: 'Paid Amount', value: rupees(order.total) },
          { label: 'Refund Status', value: 'Refund Initiated', badge: true },
          { label: 'Refund ID', value: refundIdFor(order.reference) },
          /* No instrument row. The gateway holds which card or UPI handle
             was used, and the refund goes back to it whether or not this
             app can name it - a receipt that guesses is worse than one that
             leaves it out. */
        ]}
        note="Amount will be refunded to your original payment method."
        actions={<Button label="Go to Home" onPress={onHome} />}
      />
    );
  }

  // ------------------------------------------------------------ 7. the cards

  if (stage === 'saved-cards') {
    return (
      <View style={styles.root}>
        <PageHeader
          title="Saved Cards"
          onBack={() => setStage('method')}
          action="+ Add New"
          // Adding a card means entering a PAN, which only the gateway may do.
          // So this switches the method and lets the gateway collect it, rather
          // than opening a form this app is not allowed to build.
          onAction={() => {
            setMethod('new-card');
            setStage('method');
          }}
        />

        <ScrollView
          /* resp.page, not resp.column: column is the centring inset ALONE
             and is zero on a phone, so appending it after styles.page wiped
             the gutter and ran every line into the screen edge. page is the
             same inset plus that gutter. */
          contentContainerStyle={[styles.page, resp.page]}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.list}>
            {savedCards.map(saved => (
              <MethodRow
                key={saved.id}
                leading={<BrandMark label={saved.network} size="sm" />}
                title={`•••• ${saved.last4}`}
                subtitle={`Expires ${saved.expires}`}
                badge={saved.isDefault ? 'Default' : undefined}
                selected={saved.id === card?.id}
                onPress={() => setCard(saved)}
              />
            ))}
          </View>

          <View style={styles.note}>
            <Icon name="lock" size={14} color={colors.inkMuted} />
            <Text variant="caption" style={styles.noteText}>
              Your card details are securely stored by {GATEWAY}. Homingo never
              sees or holds your card number.
            </Text>
          </View>
        </ScrollView>

        <View
        style={[
          styles.footer,
          {
            paddingBottom: footerPad(insets.bottom),
            /* Pinned outside the scroller, so it centres on the column itself
               rather than inheriting it. */
            paddingHorizontal: screenPadding + resp.sideInset,
          },
        ]}
      >
          <Button
            label="Use this card"
            onPress={() => {
              setMethod('saved-card');
              setStage('method');
            }}
          />
        </View>
      </View>
    );
  }

  // ---------------------------------------------------------- 2 + 6. method

  if (stage === 'method') {
    /*
     * "Saved Cards" is only offered when there ARE saved cards.
     *
     * There is no vault - Razorpay collects the instrument itself - so
     * `savedCards` is empty, and the row led to a list with nothing in it and
     * no way forward. "Pay with New Card" is the route that actually works.
     */
    const offered = paymentMethods.filter(
      item => item.key !== 'saved-card' || savedCards.length > 0,
    );
    const recommended = offered.filter(item => item.recommended);
    const rest = offered.filter(item => !item.recommended);

    /**
     * What a row says under its name.
     *
     * The generic blurb until the row is carrying something specific - the
     * card actually selected. "Use your saved card" above a list you have
     * already chosen from is a row that has stopped tracking what you did.
     */
    const subtitleFor = (key: PaymentMethodKey, blurb: string): string => {
      if (key === 'saved-card' && method === 'saved-card' && card) {
        return `${card.network} •••• ${card.last4}`;
      }
      return blurb;
    };

    return (
      <View style={styles.root}>
        <PageHeader
          title="Select Payment Method"
          onBack={() => setStage('summary')}
        />

        <ScrollView
          /* resp.page, not resp.column: column is the centring inset ALONE
             and is zero on a phone, so appending it after styles.page wiped
             the gutter and ran every line into the screen edge. page is the
             same inset plus that gutter. */
          contentContainerStyle={[styles.page, resp.page]}
          showsVerticalScrollIndicator={false}
        >
          {/*
            6. arriving here from a failure.

            The banner is the reason the customer is back on this list, and
            without it the page looks like it simply did not respond to the
            last Pay. It carries the same reason text the failure screen did,
            so the two never disagree.
          */}
          {payOutcome?.status === 'failed' ? (
            <View style={styles.banner}>
              <Icon name="xCircle" size={18} color={colors.danger} />
              <View style={styles.bannerBody}>
                <Text variant="labelSm" color={colors.danger}>
                  Payment failed
                </Text>
                {/* The raw reason is not shown here either - same reason as
                    the failure screen: it is written for a developer. */}
                <Text variant="caption">Please try again.</Text>
              </View>
            </View>
          ) : null}

          {/* ------------------------------------------------ what is owed */}
          <View style={styles.card}>
            <View style={styles.totalRow}>
              <Text variant="caption">Total Amount</Text>
              <Text variant="titleLg">{rupees(order.total)}</Text>
            </View>

            <Pressable
              accessibilityRole="button"
              accessibilityState={{ expanded: breakdownOpen }}
              accessibilityLabel="View price details"
              onPress={() => setBreakdownOpen(open => !open)}
              style={styles.linkRow}
            >
              <Text variant="caption" color={colors.primary}>
                {breakdownOpen ? 'Hide Details' : 'View Details'}
              </Text>
              <Icon
                name={breakdownOpen ? 'chevronDown' : 'chevronRight'}
                size={14}
                color={colors.primary}
              />
            </Pressable>

            {breakdownOpen ? <Breakdown order={order} /> : null}
          </View>

          <Countdown left={left} />

          <Text variant="eyebrow" style={styles.sectionLabel}>
            Recommended
          </Text>

          <View style={styles.list}>
            {recommended.map(item => (
              <MethodRow
                key={item.key}
                icon={item.icon}
                title={item.label}
                subtitle={item.blurb}
                selected={method === item.key}
                onPress={() => setMethod(item.key)}
              >
                {/* The app marks live inside the row rather than under the
                    list: they are what "any UPI app" means, and a strip of
                    logos floating below belongs to nothing. */}
                <View style={styles.marks}>
                  {upiApps.map(app => (
                    <BrandMark key={app.key} label={app.mark} size="sm" />
                  ))}
                </View>
              </MethodRow>
            ))}
          </View>

          <View style={styles.list}>
            {rest.map(item => (
              <React.Fragment key={item.key}>
                <MethodRow
                  icon={item.icon}
                  title={item.label}
                  subtitle={subtitleFor(item.key, item.blurb)}
                  selected={method === item.key}
                  onPress={() => setMethod(item.key)}
                />

                {/* Picking the method is not picking the card. The list of
                    cards is a detour rather than an inline expansion: three
                    rows nested under a row, all with radios, reads as one
                    list where two of the marks mean different things. */}
                {item.key === 'saved-card' && method === 'saved-card' ? (
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel="Choose a different saved card"
                    onPress={() => setStage('saved-cards')}
                    style={styles.subLink}
                  >
                    <Text variant="caption" color={colors.primary}>
                      Change card
                    </Text>
                    <Icon name="chevronRight" size={13} color={colors.primary} />
                  </Pressable>
                ) : null}
              </React.Fragment>
            ))}
          </View>

          <View style={styles.secure}>
            <Icon name="shieldCheck" size={14} color={colors.success} />
            <Text variant="caption" align="center">
              {SECURE_NOTE} · Powered by {GATEWAY}
            </Text>
          </View>
        </ScrollView>

        <View
        style={[
          styles.footer,
          {
            paddingBottom: footerPad(insets.bottom),
            /* Pinned outside the scroller, so it centres on the column itself
               rather than inheriting it. */
            paddingHorizontal: screenPadding + resp.sideInset,
          },
        ]}
      >
          <Button
            label={`Pay ${rupees(order.total)}`}
            trailingIcon="arrowRight"
            /* Unreachable: the method list is no longer routed to, because
               Razorpay collects the instrument itself. Left pointing at the
               payment run so it does something sane if it is ever revived. */
            onPress={() => {
              setPayOutcome(null);
              setStage('paying');
            }}
          />
        </View>

        {gateway()}
      </View>
    );
  }

  // --------------------------------------------------------- 1. the summary

  return (
    <View style={styles.root}>
      <PageHeader title="Booking Details" onBack={onBack} />

      <ScrollView
        /* resp.page, not resp.column: column is the centring inset ALONE
             and is zero on a phone, so appending it after styles.page wiped
             the gutter and ran every line into the screen edge. page is the
             same inset plus that gutter. */
          contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.headRow}>
          <View style={styles.chip}>
            <Text variant="eyebrow" color={colors.primary}>
              Upcoming
            </Text>
          </View>
          <Text variant="caption">Booking ID {order.reference}</Text>
        </View>

        <Text variant="titleLg">{order.title}</Text>
        <Text variant="bodySm">
          {order.when} · {order.duration}
        </Text>

        <View style={styles.addressRow}>
          <Icon name="pin" size={15} color={colors.primary} />
          <Text variant="bodySm" style={styles.addressText} numberOfLines={2}>
            {order.address}
          </Text>
        </View>

        {/* Only while there is a payment to time-box - see `expired` above. */}
        {onlinePaymentsEnabled() ? <Countdown left={left} /> : null}

        {/* ------------------------------------------------- what is booked */}
        <View style={styles.card}>
          <Pressable
            accessibilityRole="button"
            accessibilityState={{ expanded: servicesOpen }}
            accessibilityLabel={`Services, ${order.services.length} items`}
            onPress={() => setServicesOpen(open => !open)}
            style={styles.disclosure}
          >
            <Icon name="grid" size={17} color={colors.primary} />
            <View style={styles.disclosureBody}>
              <Text variant="subtitle">Services</Text>
              <Text variant="caption">
                {order.trades} · {order.services.length}{' '}
                {order.services.length === 1 ? 'service' : 'services'}
              </Text>
            </View>
            <Icon
              name={servicesOpen ? 'chevronDown' : 'chevronRight'}
              size={16}
              color={colors.borderStrong}
            />
          </Pressable>

          {servicesOpen ? (
            <View style={styles.services}>
              {order.services.map(name => (
                <View key={name} style={styles.serviceRow}>
                  <Icon name="check" size={14} color={colors.success} />
                  <Text variant="bodySm" style={styles.serviceName}>
                    {name}
                  </Text>
                </View>
              ))}
            </View>
          ) : null}
        </View>

        {/* ---------------------------------------------------- what it costs */}
        <View style={styles.card}>
          <Text variant="subtitle">Payment Details</Text>

          <View style={styles.amountRow}>
            <Text variant="bodySm">
              {onlinePaymentsEnabled() ? 'Amount to be paid' : 'Due on completion'}
            </Text>
            <Text variant="titleLg">{rupees(order.total)}</Text>
          </View>

          <Breakdown order={order} />

          <View style={styles.gatewayNote}>
            <Icon
              name={onlinePaymentsEnabled() ? 'shieldCheck' : 'wallet'}
              size={14}
              color={onlinePaymentsEnabled() ? colors.success : colors.primary}
            />
            <Text
              variant="caption"
              color={onlinePaymentsEnabled() ? colors.success : colors.primary}
            >
              {onlinePaymentsEnabled()
                ? `Secure payment by ${GATEWAY}`
                : 'Pay the professional after the work is done'}
            </Text>
          </View>
        </View>
      </ScrollView>

      <View
        style={[
          styles.footer,
          {
            paddingBottom: footerPad(insets.bottom),
            /* Pinned outside the scroller, so it centres on the column itself
               rather than inheriting it. */
            paddingHorizontal: screenPadding + resp.sideInset,
          },
        ]}
      >
        <Button
          label={onlinePaymentsEnabled() ? 'Proceed to Pay' : 'Confirm Booking'}
          trailingIcon="arrowRight"
          onPress={() => {
            /*
             * Both modes place the booking first.
             *
             * Online used to branch to the method list and open a gateway
             * before anything was booked. That order cannot work against a
             * real Razorpay - the gateway order is minted per booking from
             * the booking’s own amount, so the booking has to exist first.
             * `placing` now leads to `paying` for online and straight to the
             * receipt for cash.
             */
            if (!onlinePaymentsEnabled()) {
              /* Cash dates its confirmation from here; the online path is
                 stamped when the payment run settles. */
              setSettledAt(new Date());
            }
            setStage('placing');
          }}
        />
        {/*
          One line, shrinking rather than wrapping.

          The long form ran to two lines and sat under the gesture bar.
          `adjustsFontSizeToFit` is the safety net rather than the plan: the
          copy below fits at full size on a 360pt screen, and this only takes
          over on something narrower or at a large font setting - where a
          slightly smaller line still beats a clipped one.
        */}
        <Text
          variant="caption"
          align="center"
          numberOfLines={1}
          adjustsFontSizeToFit
        >
          {onlinePaymentsEnabled()
            ? 'Confirmed only after successful payment.'
            : 'No payment now - pay after the work is done.'}
        </Text>
      </View>
    </View>
  );

  /**
   * The gateway modal.
   *
   * A function rather than an element built once at the top: it closes over
   * `method`, `card` and `wallet`, and any of the three can change right up
   * until Pay is tapped. Rendered from every stage that can raise it - the
   * method list and the failure screen - because a `Modal` that is not mounted
   * on the branch currently returned simply never appears.
   */
  /**
   * Nothing, now - and the component behind it is left standing.
   *
   * `components/payment/RazorpayCheckout` was never Razorpay. It drew a
   * convincing gateway page, ran a 2.4 second timer and reported success,
   * which was honest while `paymentsEnabled` was false everywhere and
   * became dangerous the moment real keys existed: a customer would be told
   * "Payment Successful" with nothing charged and a booking that quietly
   * expired.
   *
   * The real sheet is native and draws itself over the app - see
   * `services/razorpay`. There is no element to mount, so the three stages
   * that called this keep the call and get nothing.
   *
   * The file stays on disk rather than being deleted: it is the reference
   * for what this flow looked like, and the method list and saved-card
   * detour it belonged to are also still here, unreachable, for the same
   * reason. Razorpay collects the instrument now.
   */
  function gateway() {
    return null;
  }
}

/** How long is left on the held slot, and how worried to be about it. */
function Countdown({ left }: { left: number }) {
  const low = left <= WINDOW_WARNING_SECONDS;
  const tint = low ? colors.danger : colors.inkMuted;

  return (
    <View
      style={[styles.countdown, low ? styles.countdownLow : null]}
      accessibilityLiveRegion="polite"
    >
      <Icon name="stopwatch" size={15} color={tint} />
      <Text variant="caption" color={tint}>
        Slot held - complete payment in {countdown(left)}
      </Text>
    </View>
  );
}

/**
 * The cart's own breakdown, replayed.
 *
 * The lines arrive already computed. Nothing here adds anything up: the total
 * the customer read on the checkout bar and the total on this page have to be
 * the same number, and the only way to guarantee that is to have one place
 * that works it out.
 */
function Breakdown({ order }: { order: PaymentOrder }) {
  return (
    <View style={styles.breakdown}>
      {order.lines.map(line => (
        <View key={line.label} style={styles.breakdownRow}>
          <Text variant="caption">{line.label}</Text>
          <Text
            variant="labelSm"
            color={line.credit ? colors.success : colors.inkBody}
          >
            {line.credit ? '− ' : ''}
            {rupees(line.amount)}
          </Text>
        </View>
      ))}

      <View style={[styles.breakdownRow, styles.breakdownTotal]}>
        <Text variant="labelSm">Total Amount</Text>
        <Text variant="labelBold">{rupees(order.total)}</Text>
      </View>
    </View>
  );
}

/** Clear of the gesture bar or the three nav buttons, which varies by device. */
const footerPad = (bottom: number) => Math.max(bottom, spacing.md);

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  page: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.lg,
    paddingBottom: spacing.xxl,
    gap: spacing.sm,
  },
  headRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  chip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceAccent,
  },
  addressRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  addressText: {
    flex: 1,
  },
  card: {
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    gap: spacing.sm,
    ...hairline,
  },
  disclosure: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  disclosureBody: {
    flex: 1,
  },
  services: {
    gap: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  serviceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  serviceName: {
    flex: 1,
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  breakdown: {
    gap: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  breakdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  breakdownTotal: {
    marginTop: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  gatewayNote: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  countdown: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  countdownLow: {
    backgroundColor: colors.dangerSurface,
  },
  banner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginTop: spacing.sm,
    padding: spacing.md,
    borderRadius: radii.lg,
    backgroundColor: colors.dangerSurface,
  },
  bannerBody: {
    flex: 1,
    gap: 2,
  },
  totalRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  linkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  sectionLabel: {
    marginTop: spacing.md,
  },
  list: {
    marginTop: spacing.sm,
    gap: spacing.sm,
  },
  marks: {
    flexDirection: 'row',
    gap: spacing.xs,
    marginTop: spacing.sm,
  },
  subLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.md,
  },
  secure: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    marginTop: spacing.xl,
  },
  /* Left-aligned inside a page that centres everything else: a checklist you
     read top to bottom needs its marks in one column, and centred rows put
     every tick in a different place. */
  steps: {
    alignSelf: 'stretch',
    gap: spacing.md,
    marginTop: spacing.xl,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  step: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  note: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginTop: spacing.lg,
    padding: spacing.md,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  noteText: {
    flex: 1,
  },
  /* Floats over the page like the cart's, so the action is always reachable
     without scrolling to the bottom of a long breakdown. */
  footer: {
    paddingHorizontal: screenPadding,
    paddingTop: spacing.md,
    gap: spacing.sm,
    backgroundColor: colors.surface,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    ...shadows.md,
  },
});

export default PaymentScreen;

